export interface DiagramNode {
  id: string;
  label: string;
  x: number;
  y: number;
  width: number;
  height: number;
  type: "process" | "decision" | "start" | "end" | "io";
  lineIndex: number; // which text line this node first appeared on
}

export interface DiagramEdge {
  from: string;
  to: string;
  label?: string;
  bidirectional?: boolean;
}

export interface Diagram {
  nodes: DiagramNode[];
  edges: DiagramEdge[];
}

/**
 * Parse text into a diagram.
 * 
 * Syntax:
 *   [Start] -> Process Step -> {Decision?} -> End Result
 *   {Decision?} -> Alternative Path -> End Result
 *   System A <-> System B   (bidirectional arrow)
 *
 * Node types:
 *   [text]  = start/end (rounded)
 *   {text?} = decision (diamond)
 *   (text)  = input/output (parallelogram)
 *   text    = process (rectangle)
 *
 * Labels on edges:
 *   A -"yes"-> B
 *   A <-> B      (bidirectional)
 */
export function parseDiagram(text: string): Diagram {
  const lines = text.split("\n").map(l => l.trim()).filter(Boolean);
  const nodeMap = new Map<string, DiagramNode>();
  const edges: DiagramEdge[] = [];

  // Parse all lines to collect nodes and edges
  for (let lineIndex = 0; lineIndex < lines.length; lineIndex++) {
    const line = lines[lineIndex];
    const parts = splitLine(line);
    for (let i = 0; i < parts.length; i++) {
      const part = parts[i];
      if (part.type === "node") {
        const key = part.label.toLowerCase();
        if (!nodeMap.has(key)) {
          nodeMap.set(key, {
            id: key,
            label: part.label,
            x: 0,
            y: 0,
            width: 0,
            height: 0,
            type: part.nodeType,
            lineIndex,
          });
        }
      }
    }
    // Build edges
    const nodeParts = parts.filter(p => p.type === "node");
    const edgeParts = parts.filter(p => p.type === "edge");
    for (let i = 0; i < nodeParts.length - 1; i++) {
      edges.push({
        from: nodeParts[i].label.toLowerCase(),
        to: nodeParts[i + 1].label.toLowerCase(),
        label: edgeParts[i]?.edgeLabel,
        bidirectional: edgeParts[i]?.bidirectional,
      });
    }
  }

  // Layout: find roots and do BFS-like layout
  const nodes = Array.from(nodeMap.values());
  layoutNodes(nodes, edges);

  return { nodes, edges };
}

interface ParsedPart {
  type: "node" | "edge";
  label: string;
  nodeType: DiagramNode["type"];
  edgeLabel?: string;
  bidirectional?: boolean;
}

function splitLine(line: string): ParsedPart[] {
  const parts: ParsedPart[] = [];
  // Match bidirectional <-> first, then labeled -"label"->, then -> and →
  const edgeRegex = /<->|<→|-"([^"]*)"->|->|→/g;

  let lastIndex = 0;
  let match;

  while ((match = edgeRegex.exec(line)) !== null) {
    const before = line.slice(lastIndex, match.index).trim();
    if (before) parts.push(parseNodePart(before));
    const isBidi = match[0] === "<->" || match[0] === "<→";
    parts.push({
      type: "edge",
      label: "",
      nodeType: "process",
      edgeLabel: match[1] || undefined,
      bidirectional: isBidi,
    });
    lastIndex = match.index + match[0].length;
  }

  const remaining = line.slice(lastIndex).trim();
  if (remaining) parts.push(parseNodePart(remaining));

  return parts;
}

function parseNodePart(text: string): ParsedPart {
  text = text.trim();
  
  // [text] = start/end
  if (text.startsWith("[") && text.endsWith("]")) {
    return { type: "node", label: text.slice(1, -1).trim(), nodeType: "start" };
  }
  // {text?} = decision
  if (text.startsWith("{") && text.endsWith("}")) {
    return { type: "node", label: text.slice(1, -1).trim(), nodeType: "decision" };
  }
  // (text) = io
  if (text.startsWith("(") && text.endsWith(")")) {
    return { type: "node", label: text.slice(1, -1).trim(), nodeType: "io" };
  }
  
  return { type: "node", label: text, nodeType: "process" };
}

function layoutNodes(nodes: DiagramNode[], edges: DiagramEdge[]) {
  if (nodes.length === 0) return;

  const NODE_W = 160;
  const NODE_H = 56;
  const GAP_X = 60;
  const GAP_Y = 80;

  // Build adjacency and find levels via BFS
  const childMap = new Map<string, string[]>();
  const parentCount = new Map<string, number>();
  
  for (const n of nodes) {
    childMap.set(n.id, []);
    parentCount.set(n.id, 0);
  }
  for (const e of edges) {
    childMap.get(e.from)?.push(e.to);
    parentCount.set(e.to, (parentCount.get(e.to) || 0) + 1);
  }

  // Find roots
  const roots = nodes.filter(n => (parentCount.get(n.id) || 0) === 0);
  if (roots.length === 0) roots.push(nodes[0]);

  const levels = new Map<string, number>();
  const queue = roots.map(r => r.id);
  roots.forEach(r => levels.set(r.id, 0));

  const visited = new Set<string>();
  while (queue.length > 0) {
    const current = queue.shift()!;
    if (visited.has(current)) continue;
    visited.add(current);
    const lvl = levels.get(current)!;
    for (const child of childMap.get(current) || []) {
      if (!levels.has(child)) {
        levels.set(child, lvl + 1);
        queue.push(child);
      } else if (levels.get(child)! < lvl + 1 && !visited.has(child)) {
        levels.set(child, lvl + 1);
        queue.push(child);
      }
    }
  }

  // Assign levels to unvisited nodes
  for (const n of nodes) {
    if (!levels.has(n.id)) levels.set(n.id, 0);
  }

  // Group by level
  const levelGroups = new Map<number, DiagramNode[]>();
  for (const n of nodes) {
    const lvl = levels.get(n.id)!;
    if (!levelGroups.has(lvl)) levelGroups.set(lvl, []);
    levelGroups.get(lvl)!.push(n);
  }

  const maxLevel = Math.max(...Array.from(levelGroups.keys()));

  for (let lvl = 0; lvl <= maxLevel; lvl++) {
    const group = levelGroups.get(lvl) || [];
    const totalWidth = group.length * NODE_W + (group.length - 1) * GAP_X;
    const startX = -totalWidth / 2;
    
    group.forEach((node, i) => {
      node.width = NODE_W;
      node.height = NODE_H;
      node.x = startX + i * (NODE_W + GAP_X);
      node.y = lvl * (NODE_H + GAP_Y);
    });
  }
}

export const EXAMPLE_DIAGRAM = `[User Opens App] -> Login Screen -> {Authenticated?}
{Authenticated?} -"yes"-> Dashboard -> Create Article
{Authenticated?} -"no"-> Registration -> Login Screen
Create Article -> (Editor) -> Review Draft -> {Approved?}
{Approved?} -"yes"-> [Published]
{Approved?} -"no"-> (Editor)`;
